<template>
  <div>
    <v-card flat v-if="!isDrag">
      <v-layout>
        <!-- guided path informations -->
        <v-flex v-if="isPathGuided && !isDrag">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <div class="grey--text">
                {{ $t('missionCard.introduction') }}
              </div>
              <v-textarea
                no-resize
                solo
                flat
                :counter="maxCharIntro"
                :disabled="isReadOnly"
                rows="3"
                :label="$t('missionCard.goal')"
                v-model="introduction[langEdition]"
                v-on="on"
                @blur="updateMissionIntroduction"
                :hint="langEdition !== locale ? introduction[locale] : null"
                :persistent-hint="showHint"
              >
                <language-translator
                  v-if="locale !== langEdition"
                  :langEdition="langEdition"
                  :textToTranslate="introduction[locale]"
                  :type="'introduction'"
                  @text-translated="updateTranslatedText"
                  slot="append"
                >
                </language-translator>
              </v-textarea>
            </template>
            <span color="primary">{{
              $t('missionCard.tooltips.introduction')
            }}</span>
          </v-tooltip>
          <media-input
            v-if="!isReadOnly"
            :label="$t('missionCard.introductionMedia')"
            :default="missionMediaIntroduction.url"
            :type="missionMediaIntroduction.type"
            :name="missionMediaIntroduction.name"
            @change-media-input="updateMedia"
            @uploading-media="uploadingMedia"
          ></media-input>
        </v-flex>
      </v-layout>
    </v-card>

    <v-card
      @click="$emit('click')"
      :color="isChosen && isDrag ? 'grey lighten-2' : 'none'"
      :class="[
        parent === 'MissionPicker' && isCardSelected
          ? 'MissionPicker__mission--selected'
          : null,
        parent === 'MissionPicker' && !isCardSelected
          ? 'MissionPicker__mission'
          : null,
        parent !== 'MissionPicker' &&
        !mission.isComplete[langEdition] &&
        !isChosen &&
        !isDrag
          ? 'draftMission'
          : null,
      ]"
      :width="full ? 600 : 170"
    >
      <v-divider />

      <v-layout row pa-2>
        <!-- mission card in mission picker-->
        <template v-if="!pathMission">
          <v-flex pl-2 pr-4>
            <h4 class="singleLine" v-if="mission.name[langEdition]">
              {{ mission.name[langEdition] }}
            </h4>
            <h4 class="singleLine" v-else>-</h4>
          </v-flex>
          <v-spacer />
          <v-layout v-if="isCardSelected" align-center justify-center>
            <span class="singleLine selectedText" v-if="full">{{
              $t('missionCard.selected')
            }}</span>
            <v-icon color="primary">mdi-checkbox-marked-circle-outline</v-icon>
          </v-layout>
          <v-layout v-if="isDeleteable" align-center justify-center>
            <v-icon color="red" @click="openTextDialog('deleteMission')"
              >mdi-delete</v-icon
            >
          </v-layout>
        </template>

        <!-- mission card in path-->
        <template v-if="pathMission">
          <v-flex xs9>
            <span> {{ $tc('missionCard.mission', indexKey + 1) }}</span>
            <h4 class="ma-0 singleLine" v-if="mission.name[langEdition]">
              {{ mission.name[langEdition] }}
            </h4>
            <h4 class="singleLine" v-else>-</h4>
          </v-flex>

          <v-spacer />

          <v-flex style="margin-right: 24px" layout align-center>
            <!-- edit mission-->
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-flex pr-2>
                  <v-icon
                    v-on="on"
                    color="primary"
                    @click="editMission"
                    :disabled="isPublicMission || isReadOnly"
                    >mdi-pen
                  </v-icon>
                </v-flex>
              </template>
              <span>{{ $t('missionCard.tooltips.modify') }}</span>
            </v-tooltip>

            <!-- locate mission-->
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-flex
                  :style="[
                    missionGeolocation
                      ? { 'min-width': '39px' }
                      : { 'padding-right': '8px' },
                  ]"
                >
                  <v-icon
                    v-on="on"
                    color="primary"
                    :disabled="isReadOnly"
                    @click="openLocationModal"
                    >mdi-map
                  </v-icon>
                  <v-icon
                    v-if="missionGeolocation"
                    size="15"
                    color="green lighten-2"
                    :disabled="isReadOnly"
                    class="checkIcon"
                    >mdi-check-circle-outline
                  </v-icon>
                </v-flex>
              </template>
              <span>{{ $t('missionCard.tooltips.localisation') }}</span>
            </v-tooltip>

            <!-- activate mission-->
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-flex pr-2>
                  <v-icon
                    v-on="on"
                    v-if="!isMissionDisabled"
                    :color="'primary'"
                    @click="disableMission"
                    >mdi-eye</v-icon
                  >
                  <v-icon
                    v-on="on"
                    v-if="isMissionDisabled"
                    @click="disableMission"
                    >mdi-eye-off</v-icon
                  >
                </v-flex>
              </template>
              <span>{{ $t('managePath.enableDisableMission') }}</span>
            </v-tooltip>

            <!-- copy mission -->
            <v-tooltip v-if="!mission.isPublic" top>
              <template v-slot:activator="{ on }">
                <v-flex pr-2>
                  <v-icon v-on="on" color="primary" @click="copyMission"
                    >mdi-content-copy</v-icon
                  >
                </v-flex>
              </template>
              <span>{{ $t('managePath.copyMission.copy') }}</span>
            </v-tooltip>

            <!-- move mission-->
            <v-tooltip top v-if="!pathMission.isFixed">
              <template v-slot:activator="{ on }">
                <v-hover 
                
                v-model="dragButtonHover" 
                open-delay="300">
                  <v-flex v-on="on">
                    <v-icon
                      v-on="on"
                      color="primary"
                      :disabled="isReadOnly"
                      class="mdi-rotate-135 pointerClass"
                      @pointerdown="elementChosenToDrag"
                    >
                      mdi-arrow-expand
                    </v-icon>
                  </v-flex>
                </v-hover>
              </template>
              <span>{{ $t('missionCard.tooltips.drag') }}</span>
            </v-tooltip>
            <!-- locked mission-->
            <v-tooltip top v-if="pathMission.isFixed">
              <template v-slot:activator="{ on }">
                <v-flex pr-2>
                  <v-icon 
                    v-on="on"
                    color="primary"
                    :disabled="isReadOnly"
                    >mdi-lock
                  </v-icon>
                </v-flex>
              </template>
              <span>{{ $t('missionCard.tooltips.lock') }}</span>
            </v-tooltip>

            <!-- delete mission from path-->
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon
                  style="padding-left: 16px"
                  v-on="on"
                  :disabled="isReadOnly"
                  @click="onRemove"
                  color="red"
                  >mdi-close
                </v-icon>
              </template>
              <span>{{ $t('missionCard.tooltips.delete') }}</span>
            </v-tooltip>
          </v-flex>
        </template>
      </v-layout>

      <v-divider />

      <!-- card picture-->
      <v-layout
        v-bind:class="{ disabled: isMissionDisabled }"
        v-if="!isDrag && !dragButtonHover"
      >
        <v-flex
          md4
          layout
          column
          align-center
          pa-0
          style="max-width: 170px; min-width: 170px; max-height: 200px; min-height: 200px;"
        >
          <v-img
            v-if="mission.pictures && mission.pictures[0].url"
            :src="mission.pictures[0].url"
            :class="
              parent === 'MissionPicker' && isCardSelected
                ? 'missionImage--selected'
                : 'missionImage'
            "
          />
          <div v-else class="emptyImage">
            <p class="emptyImageText">
              {{ $t('missionCard.noImage') }}
            </p>
          </div>
        </v-flex>
        <v-flex md8 layout column mt-0 v-if="full">
          <v-layout row wrap pa-2 align-top>
            <!-- card games-->
            <v-flex xs2>
              <span class="primary--text">{{ $t('missionCard.games') }}</span>
            </v-flex>
            <v-flex xs9 mt-4 style="padding-left: 10px">
              <span
                class="MissionCard__game mb-1"
                v-for="(game, index) in getMissionGames"
                :key="game.gameId"
              >
                <v-flex layout row>
                  <div class="pr-2">{{ index + 1 }}.</div>
                  <div v-if="game.instruction && game.instruction.text">
                    {{ game.instruction.text[langEdition] }}
                  </div>
                </v-flex>
              </span>
            </v-flex>

            <!-- card rewards-->
            <v-flex
              xs2
              v-if="getMissionRewards && getMissionRewards.length > 0"
            >
              <span class="primary--text">{{ $t('missionCard.rewards') }}</span>
            </v-flex>
            <v-flex xs9 mt-4>
              <span
                class="MissionCard__game mb-1"
                v-for="(reward, index) in getMissionRewards"
                :key="reward.rewardId"
              >
                <v-flex layout row>
                  <v-icon x-small style="margin-right: 4px">{{
                    rewardIcon[reward.type]
                  }}</v-icon>
                  <div
                    v-if="reward.type === 'xp'"
                    style="margin-right: 4px; margin-top: 3px"
                  >
                    {{ reward.xp.value }}
                  </div>
                  <div style="margin-right: 4px; margin-top: 3px">
                    {{ $t(`missionForm.reward.${reward.type}`) }}
                  </div>
                  <div
                    v-if="reward.type === 'object'"
                    style="margin-right: 4px; margin-top: 3px"
                  >
                    {{ reward.message[langEdition] }}
                  </div>
                </v-flex>
              </span>
            </v-flex>

            <!-- draft card -->
            <div
              v-if="
                parent !== 'MissionPicker' && !mission.isComplete[langEdition]
              "
              layout
              columnalign-center
              class="indiceDraftClass"
            >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-flex layout row align-center v-on="on">
                    <span class="draftMissionText">
                      {{ $t('missionCard.draft') }}
                    </span>
                    <v-icon class="spaceIcon" color="grey"
                      >mdi-file-hidden
                    </v-icon>
                  </v-flex>
                </template>
                <span>
                  {{ $t('missionCard.tooltips.draft') }}
                </span>
              </v-tooltip>
            </div>

            <!-- community card -->
            <div
              v-if="parent !== 'MissionPicker' && isPublicMission"
              layout
              columnalign-center
              class="indiceIconClass"
            >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-flex layout row align-center v-on="on">
                    <span class="communityText">
                      {{ $t('missionCard.community') }}
                    </span>
                    <v-icon class="spaceIcon" color="primary"
                      >mdi-nature-people
                    </v-icon>
                  </v-flex>
                </template>
                <span>
                  {{ $t('missionCard.tooltips.community') }}
                </span>
              </v-tooltip>
            </div>
          </v-layout>
        </v-flex>
      </v-layout>
      <span v-if="isMissionDisabled"
        ><v-icon>mdi-exclamation</v-icon
        >{{ $t('managePath.disabledMission') }}</span
      >
    </v-card>

    <!-- dialog copy mission to another path -->

    <v-dialog v-model="isCopyingMission" width="55%" lazy persistent>
      <v-card>
        <v-flex layout justify-center align-center column pl-4 pr-4 pb-2>
          <v-card-title class="headline pb-1">
            {{ $t('managePath.copyMission.title') }}
          </v-card-title>
          <span style="line-height: 120%" class="grey--text">
            {{ $t('managePath.copyMission.explanation') }}
          </span>
        </v-flex>
        <v-layout>
          <div id="dialog-copy-mission-path-list">
            <v-card
              v-for="(path, key) in Object.keys(userPaths)"
              :key="key"
              style="padding: 20px; cursor: pointer"
              @click="copyPathId = path"
              :class="copyPathId === path ? ' grey lighten-3' : ''"
            >
              {{ userPaths[path].name[langEdition] }}
              <span class="grey--text">{{
                path === pathId ? $t('managePath.copyMission.currentPath') : ''
              }}</span>
            </v-card>
          </div>
        </v-layout>
        <v-flex layout row pl-2 pr-2>
          <v-btn
            color="primary"
            outline
            class="text-none"
            @click="
              isCopyingMission = false;
              copyPathId = '';
            "
          >
            {{ $t('locationPicker.cancel') }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            class="text-none"
            :disabled="!copyPathId || copyPathId === ''"
            @click="copyMissionToPath"
          >
            {{ $t('managePath.copyMission.confirm') }}
          </v-btn>
        </v-flex>
      </v-card>
    </v-dialog>

    <!-- mission location picker dialog -->
    <v-dialog
      v-if="pathMission"
      v-model="isLocationPickerOpen"
      width="50%"
      lazy
      persistent
    >
      <v-card>
        <v-layout justify-center>
          <v-card-title class="headline">
            {{ $t('missionCard.localisation') }}
          </v-card-title>
        </v-layout>
        <v-layout justify-center>
          <location-picker
            :defaultLocation="missionGeolocation"
            :defaultRadius="20"
            :lngCenter="pathGeolocation ? pathGeolocation.coordinates[0] : 2.35"
            :latCenter="
              pathGeolocation ? pathGeolocation.coordinates[1] : 46.85
            "
            :zoomOnClick="17"
            :color="'#009999'"
            :parent="'missionCard'"
            @submit="updateLocation"
            @cancel="cancelLocationSubmit"
            @delete-location="deleteLocation"
          />
        </v-layout>
      </v-card>
    </v-dialog>

    <!-- mission delete dialog -->
    <text-dialog
      v-if="typeTextDialog"
      :type="typeTextDialog"
      :isTextDialogOpen="isTextDialogOpen"
      :title="textDialogInformations[typeTextDialog].title"
      :explanation="textDialogInformations[typeTextDialog].explanation"
      :cancelButtonText="textDialogInformations[typeTextDialog].cancel"
      :confirmButtonText="textDialogInformations[typeTextDialog].confirm"
      :confirmButtonColor="
        textDialogInformations[typeTextDialog].confirmButtonColor
      "
      @cancel="cancelTextDialog"
      @delete-mission="deleteMission"
    >
    </text-dialog>
  </div>
</template>

<script>
import LocationPicker from './LocationPicker';
import LanguageTranslator from '../components/LanguageTranslator.vue';
import MediaInput from '../components/MediaInput';
import TextDialog from '../components/TextDialog';

export default {
  name: 'MissionCard',
  components: {
    LocationPicker,
    LanguageTranslator,
    MediaInput,
    TextDialog,
  },
  props: {
    mission: {
      type: Object,
      required: true,
    },
    pathMission: {
      type: Object,
      default: null,
    },
    langEdition: {
      type: String,
      default: null,
    },
    full: {
      type: Boolean,
      default: true,
    },
    isCardSelected: {
      type: Boolean,
      default: false,
    },
    isDeleteable: {
      type: Boolean,
      default: false,
    },
    pathId: {
      type: String,
      default: '',
    },
    indexKey: {
      type: Number,
      default: 0,
    },
    isDrag: {
      type: Boolean,
      default: false,
    },
    parent: {
      type: String,
      default: null,
    },
    showHint: {
      type: Boolean,
      default: false,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dragButtonHover: false,
      published: null,
      isLocationPickerOpen: false,
      locationPickerKey: 0,
      missionGeolocation: null,
      isCopyingMission: false,
      copyPathId: '',
      editCard: false,
      introduction: { fr: '', en: '' },
      newLocationMission: this.$t('upcomingFeature.locationMissionIcon'),
      locationMissionFeatureType: 'icon',
      isChosen: false,
      isMissionDisabled: false,
      //isFixed: false,
      missionMediaIntroduction: {},
      maxCharIntro: 300,
      typeTextDialog: '',
      isTextDialogOpen: false,
      gameIcon: {
        goTo: 'mdi-map-marker-radius',
        find: 'mdi-eye',
        qrcode: 'mdi-qrcode-scan',
        flashCode: 'mdi-qrcode-scan',
        takePicture: 'mdi-camera',
        survey: 'mdi-comment-question-outline',
        takeSelfie: 'mdi-face',
        targetZone: 'mdi-gesture-double-tap',
        mcq: 'mdi-format-list-checks',
        mcqImage: 'mdi-image-multiple',
        mcqSurvey: 'mdi-account-search',
        sort: 'mdi-sort',
        sortImage: 'mdi-sort',
        scrapPicture: 'mdi-eraser',
        anagram: 'mdi-textbox-password',
        textInput: 'mdi-textbox',
        puzzle: 'mdi-puzzle',
      },
      rewardIcon: {
        infosheet: 'mdi-information-outline',
        xp: 'mdi-plus', //'mdi-plus',
        object: 'mdi-toolbox',
      },
    };
  },
  watch: {
    // eslint-disable-next-line

    isDrag() {
      if (this.isDrag === false) {
        this.isChosen = false;
      }
    },
    pathMission() {
      // mise à jour des données lors du drag and drop //mieux dans une fonction
      if (!this.pathMission.introduction) {
        this.introduction = { fr: '', en: '' };
      } else {
        this.path.langsEdited.forEach((lang) => {
          this.introduction[lang] = this.pathMission.introduction[lang];
        });
      }
      if (this.pathMission.geolocationInPath) {
        this.missionGeolocation = this.pathMission.geolocationInPath;
      }
    },
  },
  created() {
    if (!this.pathMission) {
      return;
    }
    this.published = this.pathMission && Boolean(this.pathMission.isPublished);
    if (!this.pathMission.introduction) {
      this.introduction = { fr: '', en: '' };
    } else {
      this.path.langsEdited.forEach((lang) => {
        this.introduction[lang] = this.pathMission.introduction[lang];
      });
    }
    if (this.pathMission.geolocationInPath) {
      this.missionGeolocation = this.pathMission.geolocationInPath;
    }
    if (this.pathMission.introductionMedia) {
      this.missionMediaIntroduction = this.pathMission.introductionMedia;
    }
    if (this.pathMission.hasOwnProperty('isActive')) {
      this.isMissionDisabled = !this.pathMission.isActive;
    } else {
      this.isMissionDisabled = false;
    }
    //if (this.pathMission.hasOwnProperty('isFixed')) {
    //  this.isFixed = this.pathMission.isFixed;
    //}
  },
  computed: {
    path() {
      if (this.pathId) {
        return this.$store.getters['paths/getPathById'](this.pathId);
      }
      return null;
    },
    pathGeolocation() {
      if (this.path && this.path.geolocation) {
        return this.path.geolocation;
      }
      return null;
    },
    isPathGuided() {
      if (this.$store.getters['paths/getPathById'](this.pathId)) {
        return this.$store.getters['paths/getPathById'](this.pathId).isGuided;
      }
      return '';
    },
    getMissionGames() {
      return this.mission.games.map(
        (gameId) => this.$store.state.missions.games[gameId]
      );
    },
    getMissionRewards() {
      return this.mission.rewards;
    },
    isPublicMission() {
      return this.mission.isPublic === true;
    },
    locale() {
      return this.$i18n.locale;
    },
    textDialogInformations() {
      return {
        deleteMission: {
          title: this.$t(
            'settingsBar.textDialogInformations.deleteMission.title'
          ),
          explanation: this.$t(
            'settingsBar.textDialogInformations.deleteMission.explanation'
          ),
          cancel: this.$t(
            'settingsBar.textDialogInformations.deleteMission.cancel'
          ),
          confirm: this.$t(
            'settingsBar.textDialogInformations.deleteMission.confirm'
          ),
          confirmButtonColor: 'red',
        },
      };
    },
    userPaths() {
      // let paths = JSON.parse(JSON.stringify(this.$store.state.paths.paths));
      let paths = this.$store.state.paths.paths;

      return paths;
    },
  },
  methods: {
    getRequiredRule() {
      return [(data) => Boolean(data) || this.$t('pathForm.errors.required')];
    },
    getIntroductionRules() {
      return [
        ...this.getRequiredRule(),
        (introduction) =>
          introduction.length <= this.maxCharIntro ||
          this.$t('missionCard.errors.introductionTooLong'),
      ];
    },
    openTextDialog(type) {
      this.typeTextDialog = type;
      this.isTextDialogOpen = true;
      this.$emit('un-select');
    },
    cancelTextDialog() {
      this.isTextDialogOpen = false;
    },
    copyMissionToPath() {
      this.$emit(
        'copyMissionToPath',
        this.copyPathId,
        this.pathMission.missionId
      );
      this.isCopyingMission = false;
    },
    uploadingMedia(isUploading, percentProgress) {
      this.$emit('uploading-media', isUploading, percentProgress);
    },
    updateMedia(mediaInfo) {
      const payload = {
        introductionMedia: {
          url: mediaInfo.url,
          name: mediaInfo.name,
          type: mediaInfo.type,
        }
      }
      this.updateMission(payload);
    },
    updateIsActive() {
      const payload = { isActive: !this.isMissionDisabled }
      this.updateMission(payload);
    },
    updateMissionIntroduction() {
      if (
        this.introduction &&
        (this.introduction[this.langEdition].length > this.maxCharIntro ||
          (this.pathMission &&
            this.pathMission.introduction &&
            this.pathMission.introduction[this.langEdition] &&
            this.pathMission.introduction[this.langEdition] ===
              this.introduction[this.langEdition]))
      ) {
        return;
      }
      const payload = { introduction: this.introduction }
      this.updateMission(payload);
    },
    updateLocation(location) {
      this.isLocationPickerOpen = false;
      const newLocation = {
        type: 'point',
        coordinates: [location.latlng.lng, location.latlng.lat],
      };
      this.missionGeolocation = newLocation;
      const payload = { geolocationInPath: newLocation }
      this.updateMission(payload);
    },
    deleteLocation() {
      this.isLocationPickerOpen = false;
      this.missionGeolocation = null;
      const payload = { geolocationInPath: null }
      this.updateMission(payload);
    },
    async updateMission(payload) {
      this.$store.commit('app/SET_LOADING');
      try {
        await this.$store.dispatch('paths/UPDATE_MISSION', {
          pathId: this.pathId,
          kuzzle: this.$kuzzle,
          index: this.$store.getters['auth/currentUserIndex'],
          missionId: this.pathMission.missionId,
          indexKey: this.indexKey,
          payload: payload
        });
      } catch (error) {
        console.error(error.message);
        this.$toast.show(
          this.$t('pathManagement.errors.updateMissionIntroduction'),
          this.$t('common.error')
        );
      }
      this.$store.commit('app/UNSET_LOADING');
    },
    onRemove() {
      this.$emit('onRemove', {
        missionId: this.pathMission.missionId,
        indexKey: this.indexKey,
        forced: false,
      });
    },
    openLocationModal() {
      this.isLocationPickerOpen = true;
    },
    cancelLocationSubmit() {
      this.isLocationPickerOpen = false;
    },
    disableMission() {
      this.isMissionDisabled = !this.isMissionDisabled;
      this.updateIsActive();
    },
    copyMission() {
      console.log('copy mission');
      this.isCopyingMission = !this.isCopyingMission;
    },
    async deleteMission() {
      try {
        await this.$store.dispatch('missions/DELETE_MISSION', {
          kuzzle: this.$kuzzle,
          index: this.$store.getters['auth/currentUserIndex'],
          missionId: this.mission.key,
        });
      } catch (error) {
        console.error(error.message);
        this.$toast.show(this.$t('pathForm.errors.delete'));
      }
    },
      
    elementChosenToDrag() {
      this.isChosen = true;
      this.$emit('drag-button-click');
    },

    editMission() {
      localStorage.setItem('pathLang', this.langEdition);
      this.$router.push(
        `/editMission/${this.pathId}/${this.pathMission.missionId}`
      );
    },

    async updateTranslatedText(textTranslated, type) {
      if (textTranslated) {
        if (type === 'introduction') {
          this.introduction[this.langEdition] = textTranslated;
          this.updateMissionIntroduction();
        }
      }
    },
  },
  watch: {
    pathMission(newValue) {
      if (typeof this.pathMission.isActive === 'undefined') {
        this.isMissionDisabled = false;
      } else {
        this.isMissionDisabled = !this.pathMission.isActive;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
.singleLine
  text-overflow: ellipsis
  white-space: nowrap
  overflow: hidden
  width: auto
  display: block

.emptyImage
  background-image: linear-gradient(to bottom, #EEEEEE, 80%, #E0E0E0);
  height: 180px
  width: 150px
  display: flex
  align-items: center
  justify-content: center

.emptyImageText
  font-size: medium;
  text-align: center;
  line-height: 120%;
  color: grey;

#dialog-copy-mission-path-list
  padding: 20px;
  margin: 20px;
  width: 100%;

.rounded-card
    border-radius: 6px

.rounded-img
    border-bottom-left-radius: 6px

.v-input--selection-controls
    margin: 0
    padding: 0

.MissionCard__game
  display: block
  font-size: 12px

.icons-container
  i
    margin-left: 0.4em

.pointerClass
  cursor: pointer

.missionImage
  max-height: 200px
  min-height: 200px
  max-width: 170px
  min-width: 170px

.missionImage--selected
  max-height: 195px
  min-height: 195px
  max-width: 162px
  min-width: 162px

.MissionPicker__mission--selected
  border: 4px solid #009999 !important
  margin-left: 4px
  margin-right: 4px
  margin-top: 4px
  margin-bottom: 8px

.MissionPicker__mission
  margin-bottom: 10px;
  padding-left: 4px

.disabled
  opacity: 0.3;

.indiceIconClass
  position: absolute
  bottom: 15px;
  left: 78%
  right: 5%

.indiceDraftClass
  position: absolute
  bottom: 225px;
  left: 40%

.draftMission
  border: 4px solid lightgrey !important

.draftMissionText
  font-size: medium;
  text-align: center;
  line-height: 100%;
  color: grey

.communityText
  font-size: small;
  text-align: center;
  line-height: 100%;
  color: #009999;
.selectedText
  padding-right: 10px
  color: #009999
.spaceIcon
  margin-left: 5px;
.checkIcon
  position: relative
  top: 10%
  right: 12%
</style>
